// Standardmaterial (E3, S5; Mockup /crm/material; Gernots Entscheidung 1:
// Günther tippt die echte Liste selbst ein): was bei jeder Anlage dabei ist –
// das Büro pflegt die Liste selbst, ohne Programmieren.
//
// Zwei Abschnitte wie im Mockup: „Bei jeder Anlage dabei“ (kommt von selbst
// in jede Materialliste) und „Zum Antippen draußen“ (in der HE-Doku eine
// Kachel; im Büro „+ aus der Auswahl“) – je mit einem Satz, was mit ihren
// Einträgen geschieht (seit E3c, S7: G21; bis dahin „Immer dabei“ und „Zur
// Auswahl“ mit einem gelben Hinweis oben); darunter zugeklappt, was
// herausgenommen wurde. Jede Zeile
// wird direkt geändert – gespeichert wird beim Verlassen des Feldes (Tab,
// Klick daneben, Enter), mit einem kleinen Haken. „+ Eintrag“ legt eine neue
// Zeile an (Kennung im Browser); gespeichert wird sie, sobald sie eine
// Bezeichnung hat. Nichts wird gelöscht: „aus der Liste nehmen“ setzt den
// Eintrag inaktiv, „wieder aufnehmen“ holt ihn zurück. Was schon freigegeben
// ist, bleibt, wie es war – Freigaben tragen Kopien (buero-vertrag.ts).
//
// Seit dem Augentest am 21.10.2026 (Gernot): oben ein kurzer Satz statt des
// Absatzes; Spaltenköpfe und Felder in einem gemeinsamen Raster, am Handy je
// Feld ein Etikett; kein Platzhalter „freiwillig“ mehr; der Verschiebe-Knopf
// sagt, wohin („→ Zur Auswahl“ / „→ Immer dabei“); „Herausgenommen (n)“ ist
// zugeklappt, damit die Liste nicht immer länger wird. Verhalten und
// Server-Aufrufe sind gleich geblieben.
//
// Seit E3b (O65): Im Abschnitt „Zur Auswahl“ steht dazu die Gruppe –
// Einträge mit derselben Gruppe sind in der Doku-App EINE Kachel mit ihren
// Varianten („Kabelkanal“ → 40×60, 60×60 …). „Immer dabei“ steht draußen
// nicht, dort gibt es die Spalte nicht.
//
// Die Liste lebt nach dem ersten Laden hier im Browser weiter und wird nicht
// bei jedem Speichern neu geholt: sonst sortierte der Server die Zeilen um,
// während jemand tippt.
//
// Seit dem Augentest E3b (Gernot, 23.10.2026) ist sie geordnet – nach
// Gruppe, dann Bezeichnung, Zahlen der Reihe nach (liste.ts,
// `zeilenOrdnen`): beim Laden und wenn eine Zeile den Abschnitt wechselt
// oder wieder aufgenommen wird, nie beim Tippen. Eine neue Zeile steht bis
// zum nächsten Öffnen der Seite unten.

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { BUERO_GRENZEN, neueKennung, type BueroAntwort, type MaterialArt, type StandardlisteAntwort } from "@happy-energy/kern";
import { Chip, Ikon, senden, Spaeter } from "@happy-energy/oberflaeche";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { meldungAus } from "../meldung.ts";
import { Seite } from "../rahmen.tsx";
import {
  ABSCHNITT,
  ABSCHNITT_SATZ,
  abschnitte,
  alsGespeichert,
  eingabeAus,
  hatMeldung,
  mussSpeichern,
  neueZeile,
  sammelStatus,
  verschieben,
  zeilenOrdnen,
  zeileVomServer,
  type Entwurf,
  type ListenZeile,
  type Status,
} from "./liste.ts";
import "../formular.css";
import "./standardliste.css";

const ROLLEN_MELDUNG = "Die Standardliste pflegen Büro und Leitung.";

/** Die Überschriften der Spalten – oben im Kopf und, am Handy, als Etikett über jedem Feld. */
const SPALTE: Record<keyof Entwurf, ReactNode> = {
  bezeichnung: "Bezeichnung",
  menge: "Menge",
  einheit: "Einheit",
  artikelnummer: (
    <>
      Artikelnummer <span className="freiwillig">(wenn vorhanden)</span>
    </>
  ),
  gruppe: (
    <>
      Gruppe <span className="freiwillig">(für Varianten)</span>
    </>
  ),
};

/** Dieselben Einträge ohne diesen einen. */
function ohne<T>(alle: Record<string, T>, id: string): Record<string, T> {
  const rest = { ...alle };
  delete rest[id];
  return rest;
}

function StatusAnzeige({ s }: { s: Status | undefined }) {
  if (!s) return null;
  if (s.art === "speichert") return <span className="kreisel std-kreisel" aria-label="wird gespeichert" />;
  if (s.art === "gespeichert")
    return (
      <span className="gespeichert std-gespeichert" role="status" title="gespeichert">
        <Ikon name="haken" groesse="k" />
        <span className="std-status-wort">gespeichert</span>
      </span>
    );
  return null;
}

function Zeile({
  z,
  status,
  fokus,
  mitGruppe,
  aendern,
  verlassen,
  umschalten,
  herausnehmen,
}: {
  z: ListenZeile;
  status: Status | undefined;
  fokus: boolean;
  /** Nur „Zur Auswahl“: die Gruppe für Varianten draußen. */
  mitGruppe: boolean;
  aendern: (feld: keyof Entwurf, wert: string) => void;
  verlassen: () => void;
  umschalten: () => void;
  herausnehmen: () => void;
}) {
  const fehlt = status?.art === "fehlt" || status?.art === "fehler" ? status.meldung : null;
  const v = verschieben(z.art);
  const feld = (name: keyof Entwurf, extra: { max: number; klasse: string; platzhalter?: string; zahl?: boolean }) => (
    <label className={`crm-feld std-feld std-f-${name}`}>
      <span className="std-etikett">{SPALTE[name]}</span>
      <input
        className={`crm-eingabe ${extra.klasse}`}
        value={z.entwurf[name]}
        maxLength={extra.max}
        placeholder={extra.platzhalter}
        inputMode={extra.zahl ? "decimal" : undefined}
        autoFocus={name === "bezeichnung" && fokus}
        onChange={(e) => aendern(name, e.target.value)}
        onBlur={verlassen}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
      />
    </label>
  );
  return (
    <li className={`std-zeile${fehlt ? " mit-meldung" : ""}`}>
      {feld("bezeichnung", { max: BUERO_GRENZEN.bezeichnung, klasse: "std-bezeichnung", platzhalter: "z. B. Kabelbinder UV-beständig" })}
      {mitGruppe && feld("gruppe", { max: BUERO_GRENZEN.gruppe, klasse: "std-gruppe", platzhalter: "z. B. Kabelkanal" })}
      {feld("menge", { max: 12, klasse: "std-menge", zahl: true })}
      {feld("einheit", { max: BUERO_GRENZEN.einheit, klasse: "std-einheit", platzhalter: "Stk" })}
      {feld("artikelnummer", { max: BUERO_GRENZEN.artikelnummer, klasse: "std-artikel" })}
      <div className="std-knoepfe">
        <button type="button" className="crm-knopf rand klein" onClick={umschalten} title={v.erklaerung} aria-label={v.erklaerung}>
          {v.knopf}
        </button>
        <button
          type="button"
          className="crm-knopf leise klein"
          onClick={herausnehmen}
          title={z.gespeichert === null ? "Den neuen Eintrag nicht anlegen" : "Kommt bei neuen Freigaben nicht mehr vor – bleibt unten unter „Herausgenommen“"}
        >
          {z.gespeichert === null ? "verwerfen" : "aus der Liste nehmen"}
        </button>
        <span className="std-status">
          <StatusAnzeige s={status} />
        </span>
      </div>
      {fehlt && (
        <p className={status?.art === "fehler" ? "crm-fehler-satz std-meldung" : "std-meldung std-fehlt"} role={status?.art === "fehler" ? "alert" : undefined}>
          {z.gespeichert === null && status?.art === "fehlt" ? `Noch nicht gespeichert – ${fehlt}` : fehlt}
        </p>
      )}
    </li>
  );
}

function Abschnitt({
  satz,
  art,
  zeilen,
  status,
  neuId,
  zeileProps,
  hinzufuegen,
}: {
  satz: string;
  art: MaterialArt;
  zeilen: ListenZeile[];
  status: Record<string, Status>;
  neuId: string | null;
  zeileProps: (z: ListenZeile) => Omit<Parameters<typeof Zeile>[0], "z" | "status" | "fokus" | "mitGruppe">;
  hinzufuegen: (art: MaterialArt) => void;
}) {
  const titel = ABSCHNITT[art];
  return (
    <section className="crm-karte std-abschnitt" aria-label={titel}>
      <div className="karten-kopf">
        <Ikon name={art === "immer" ? "haken" : "liste"} />
        <h2>{titel}</h2>
        <Chip>{zeilen.length === 1 ? "1 Eintrag" : `${zeilen.length} Einträge`}</Chip>
      </div>
      <p className="std-satz">{satz}</p>
      {zeilen.length > 0 ? (
        // Kopf und Zeilen in einem Raster – jede Überschrift steht genau über ihrem Feld (standardliste.css).
        <div className={`std-raster${art === "auswahl" ? " mit-gruppe" : ""}`}>
          <div className="std-kopf" aria-hidden="true">
            <span>{SPALTE.bezeichnung}</span>
            {art === "auswahl" && <span>{SPALTE.gruppe}</span>}
            <span>{SPALTE.menge}</span>
            <span>{SPALTE.einheit}</span>
            <span>{SPALTE.artikelnummer}</span>
          </div>
          <ul className="std-liste">
            {zeilen.map((z) => (
              <Zeile key={z.id} z={z} status={status[z.id]} fokus={z.id === neuId} mitGruppe={art === "auswahl"} {...zeileProps(z)} />
            ))}
          </ul>
        </div>
      ) : (
        <p className="leise klein std-satz">Noch nichts – „+ Eintrag“ legt den ersten an.</p>
      )}
      <div>
        <button type="button" className="crm-knopf rand klein" onClick={() => hinzufuegen(art)} title={`Neuen Eintrag unter „${titel}“ anlegen`}>
          <Ikon name="plus" groesse="k" /> Eintrag
        </button>
      </div>
    </section>
  );
}

/**
 * Was aus der Liste genommen wurde – zugeklappt, damit die Liste nicht immer
 * länger wird (Gernot, Augentest 21.10.2026). Steht nur da, wenn es etwas
 * gibt; jedes Mal, wenn der Abschnitt neu erscheint, ist er wieder zu. Hat
 * eine Zeile darin eine Meldung (z. B. „Nicht gespeichert“), klappt er von
 * selbst auf – sonst bliebe sie verborgen.
 */
function Herausgenommen({ zeilen, status, wiederAufnehmen }: { zeilen: ListenZeile[]; status: Record<string, Status>; wiederAufnehmen: (z: ListenZeile) => void }) {
  const [auf, setAuf] = useState(false);
  const inhalt = useId();
  const sammel = sammelStatus(zeilen.map((z) => status[z.id]));
  const offen = auf || hatMeldung(sammel);
  return (
    <section className="crm-karte std-inaktiv" aria-label="Herausgenommen">
      <div className="karten-kopf">
        <h2>
          <button type="button" className="std-klapp" aria-expanded={offen} aria-controls={inhalt} onClick={() => setAuf(!offen)}>
            <Ikon name="rechts" groesse="k" />
            <span>
              Herausgenommen <span className="std-klapp-zahl">({zeilen.length})</span>
            </span>
          </button>
        </h2>
        {!offen && <StatusAnzeige s={sammel} />}
      </div>
      <div id={inhalt} className="std-klapp-inhalt" hidden={!offen}>
        <p className="leise klein std-satz">Bleibt erhalten, damit alte Freigaben stimmen. Kommt bei neuen Freigaben nicht mehr vor.</p>
        <ul className="std-inaktiv-liste">
          {zeilen.map((z) => {
            const s = status[z.id];
            return (
              <li key={z.id}>
                <span className="std-inaktiv-wort">
                  <b>{z.entwurf.bezeichnung}</b> · {z.entwurf.menge} {z.entwurf.einheit} · vorher unter „{ABSCHNITT[z.art]}“
                </span>
                <button type="button" className="crm-knopf rand klein" onClick={() => wiederAufnehmen(z)} title={`Zurück unter „${ABSCHNITT[z.art]}“`}>
                  <Ikon name="zurueck" groesse="k" /> wieder aufnehmen
                </button>
                <StatusAnzeige s={s} />
                {hatMeldung(s) && <p className="crm-fehler-satz std-meldung">{s.meldung}</p>}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export function Standardmaterial() {
  const { daten, fehler, neu } = useLaden<StandardlisteAntwort>("/api/crm/standardmaterial");
  // Seit dem Augentest E3b geordnet: nach Gruppe, dann Bezeichnung, Zahlen der Reihe nach (liste.ts, zeilenOrdnen).
  const vomServer = useMemo(() => (daten ? zeilenOrdnen(daten.eintraege.map(zeileVomServer)) : null), [daten]);
  // Nach der ersten Änderung lebt die Liste hier – vorher ist sie die vom Server.
  const [eigene, setEigene] = useState<ListenZeile[] | null>(null);
  const liste = eigene ?? vomServer;
  const [status, setStatus] = useState<Record<string, Status>>({});
  const [neuId, setNeuId] = useState<string | null>(null);

  // Für das Speichern im Hintergrund: die jüngste Liste, und je Zeile, ob gerade gespeichert wird.
  const aktuell = useRef<ListenZeile[] | null>(null);
  useEffect(() => {
    aktuell.current = liste;
  }, [liste]);
  const laeuft = useRef(new Set<string>());
  const nochmal = useRef(new Set<string>());
  const uhren = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const alle = uhren.current;
    return () => {
      for (const u of alle.values()) clearTimeout(u);
    };
  }, []);

  const setzeStatus = useCallback((id: string, s: Status | null) => {
    setStatus((alt) => (s ? { ...alt, [id]: s } : ohne(alt, id)));
  }, []);

  const zeileAendern = useCallback(
    (id: string, f: (z: ListenZeile) => ListenZeile | null) => {
      setEigene((alt) => {
        const basis = alt ?? vomServer ?? [];
        const neu: ListenZeile[] = [];
        for (const z of basis) {
          if (z.id !== id) neu.push(z);
          else {
            const x = f(z);
            if (x) neu.push(x);
          }
        }
        return neu;
      });
    },
    [vomServer],
  );

  /**
   * Speichert eine Zeile, wenn sie gültig ist und sich etwas geändert hat –
   * je Zeile eins nach dem anderen: Läuft für sie schon eine Anfrage, kommt
   * danach der jüngste Stand hinterher (wer schnell weitertippt, verliert
   * nichts, und keine ältere Anfrage überholt eine neuere).
   */
  const speichern = useCallback(
    async (anfang: ListenZeile) => {
      const id = anfang.id;
      if (laeuft.current.has(id)) {
        nochmal.current.add(id);
        return;
      }
      laeuft.current.add(id);
      try {
        let z: ListenZeile | undefined = anfang;
        while (z) {
          const pruefung = eingabeAus(z);
          if (!pruefung.ok) {
            setzeStatus(id, { art: "fehlt", meldung: pruefung.meldung });
            break;
          }
          if (mussSpeichern(z, pruefung.eingabe)) {
            setzeStatus(id, { art: "speichert" });
            try {
              await senden<BueroAntwort>("/api/crm/standardmaterial", pruefung.eingabe);
            } catch (f) {
              setzeStatus(id, { art: "fehler", meldung: `Nicht gespeichert: ${meldungAus(f, ROLLEN_MELDUNG)}` });
              break;
            }
            const gespeichert = alsGespeichert(pruefung.eingabe);
            zeileAendern(id, (x) => ({ ...x, gespeichert }));
            setzeStatus(id, { art: "gespeichert" });
            const alt = uhren.current.get(id);
            if (alt) clearTimeout(alt);
            uhren.current.set(
              id,
              setTimeout(() => setStatus((st) => (st[id]?.art === "gespeichert" ? ohne(st, id) : st)), 2500),
            );
          } else {
            // Nichts zu speichern – eine alte Meldung an der Zeile gilt nicht mehr.
            setStatus((st) => (st[id]?.art === "fehlt" || st[id]?.art === "fehler" ? ohne(st, id) : st));
          }
          z = nochmal.current.delete(id) ? aktuell.current?.find((x) => x.id === id) : undefined;
        }
      } finally {
        laeuft.current.delete(id);
        nochmal.current.delete(id);
      }
    },
    [setzeStatus, zeileAendern],
  );

  function hinzufuegen(art: MaterialArt) {
    const z = neueZeile(neueKennung(), art);
    setEigene((alt) => [...(alt ?? vomServer ?? []), z]);
    setNeuId(z.id);
  }

  function zeileProps(z: ListenZeile) {
    return {
      aendern: (feld: keyof Entwurf, wert: string) => zeileAendern(z.id, (x) => ({ ...x, entwurf: { ...x.entwurf, [feld]: wert } })),
      verlassen: () => {
        // Der Fokus gehört der neuen Zeile nur einmal – zieht sie in den anderen Abschnitt um, springt er nicht mit.
        if (neuId === z.id) setNeuId(null);
        void speichern(z);
      },
      umschalten: () => {
        const neu: ListenZeile = { ...z, art: verschieben(z.art).nach };
        // Im anderen Abschnitt an ihrem Platz in der Reihenfolge (seit dem Augentest E3b; vorher ans Ende).
        // Neu geordnet wird nur bei einem Klick wie diesem, nie beim Tippen – sonst spränge die Zeile unter dem Finger weg.
        setEigene((alt) => zeilenOrdnen([...(alt ?? vomServer ?? []).filter((x) => x.id !== z.id), neu]));
        void speichern(neu);
      },
      herausnehmen: () => {
        if (z.gespeichert === null) {
          zeileAendern(z.id, () => null);
          setzeStatus(z.id, null);
          return;
        }
        const neu: ListenZeile = { ...z, aktiv: false };
        zeileAendern(z.id, () => neu);
        void speichern(neu);
      },
    };
  }

  function wiederAufnehmen(z: ListenZeile) {
    const neu: ListenZeile = { ...z, aktiv: true };
    setEigene((alt) => zeilenOrdnen([...(alt ?? vomServer ?? []).filter((x) => x.id !== z.id), neu]));
    void speichern(neu);
  }

  const teile = liste ? abschnitte(liste) : null;

  return (
    <Seite
      titel="Standardmaterial"
      unter="Die Vorlage für die Materialliste jeder Anlage – hier pflegst du sie selbst"
      knoepfe={teile ? <span className="leise klein">{teile.immer.length + teile.auswahl.length} Einträge in der Liste</span> : <span />}
    >
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!liste && fehler === undefined && <Laedt />}
      {teile && (
        <>
          <p className="leise klein std-oben">Speichert von selbst. Schon freigegebene Materiallisten ändern sich dadurch nicht.</p>
          <Abschnitt
            satz={ABSCHNITT_SATZ.immer}
            art="immer"
            zeilen={teile.immer}
            status={status}
            neuId={neuId}
            zeileProps={zeileProps}
            hinzufuegen={hinzufuegen}
          />
          <Abschnitt
            satz={ABSCHNITT_SATZ.auswahl}
            art="auswahl"
            zeilen={teile.auswahl}
            status={status}
            neuId={neuId}
            zeileProps={zeileProps}
            hinzufuegen={hinzufuegen}
          />
          {teile.inaktiv.length > 0 && <Herausgenommen zeilen={teile.inaktiv} status={status} wiederAufnehmen={wiederAufnehmen} />}
          <div className="std-spaeter">
            <Spaeter titel="Ein Standard für alle Firmen" wann="Stufe 3 · Franchise">
              Happy Energy legt den Standard fest, jede Firma übernimmt ihn – und ergänzt nur ihre Eigenheiten.
            </Spaeter>
            <Spaeter titel="Artikelnummern für die Bestellung" wann="später">
              Damit aus der Liste direkt Bestellungen werden – die Artikelnummer kannst du schon heute eintragen.
            </Spaeter>
          </div>
        </>
      )}
    </Seite>
  );
}
